import * as React from 'react';

import {
  useHover,
  useFocus,
  useRole,
  useDismiss,
  useInteractions,
  useDelayGroupContext,
  useDelayGroup,
  useId,
  useTransitionStyles,
  UseDismissProps,
} from '@floating-ui/react';
import {usePrefersReducedMotion} from '@packages/systems/spring-hooks';

import {useFloatingBase, type UseFloatingBaseProps} from '../../hooks';

type TooltipContextTypes = null | ReturnType<typeof useTooltip>;

/**
 * Used to communicate state between Tooltip trigger & content.
 * Defined here to avoid circular dependency w/ `Tooltip.jsx`
 */
export const TooltipContext = React.createContext<TooltipContextTypes>(null);

type Delay = ReturnType<typeof useDelayGroupContext>['delay'];

export const TOOLTIP_DELAY = {
  instant: {
    open: 0,
    close: 0,
  },
  short: {
    open: 250,
    close: 1,
  },
  medium: {
    open: 450,
    close: 1,
  },
  hoverable: {
    open: 450,
    close: 250,
  },
} as const;

const TOOLTIP_REST_DELAY_MS = 250;

// How far the tooltip should move when appearing.
const SLIDE_DISTANCE = '4px';

const useTooltipContext = () => {
  const context = React.useContext(TooltipContext);

  if (context == null) {
    throw new Error('Tooltip context not set up yet');
  }

  return context;
};

export type UseTooltipProps = UseFloatingBaseProps & {
  delay?: Exclude<keyof typeof TOOLTIP_DELAY, 'hoverable'>;
  horizontalOffset?: number;
  isHoverable?: boolean;
  shouldCloseOnClick?: boolean;
  _useDismissProps?: UseDismissProps;
  verticalOffset?: number;
};

const dismissDefaultProps = {
  escapeKey: true,
  bubbles: false,
};

export const useTooltip = ({
  defaultIsOpen,
  delay: delayProp = 'medium',
  horizontalOffset,
  isDisabled,
  isOpen: controlledIsOpen,
  onOpenChange,
  placement,
  maxWidth = 280,
  minWidth = 'min-content',
  arrowRef,
  isHoverable,
  shouldCloseOnClick,
  shouldEscapeViewport,
  _useDismissProps = dismissDefaultProps,
  verticalOffset,
}: UseTooltipProps) => {
  const offsetModifier: UseFloatingBaseProps['offsetModifier'] =
    React.useCallback(() => {
      if (horizontalOffset === undefined && verticalOffset === undefined) {
        return null;
      }

      const offsetModifiers = {
        horizontalOffset,
        verticalOffset,
      };

      return offsetModifiers;
    }, [horizontalOffset, verticalOffset]);

  const data = useFloatingBase({
    arrowRef,
    defaultIsOpen,
    isOpen: controlledIsOpen,
    isDisabled,
    maxWidth,
    minWidth,
    offsetModifier,
    onOpenChange,
    placement,
    shouldEscapeViewport,
  });

  const {delay: groupDelay} = useDelayGroupContext();
  const {context} = data;

  /**
   * We want to open the Tooltip after 250ms if they stop their mouse movement, signaling intention (`useHover.restMs`).
   * If they keep moving their mouse but stay within the element, we open after 450ms anyway (`BASE_TOOLTIP_DELAY.open`).
   *
   * However, if we are part of a `TooltipGroup`, then use their `groupDelay` timing, as it will sync between tooltips.
   * */
  let delay: Delay = TOOLTIP_DELAY[delayProp];
  const isInDelayGroup = groupDelay !== 0;

  if (!isInDelayGroup && isHoverable) {
    delay = {
      open: TOOLTIP_DELAY[delayProp].open,
      close: TOOLTIP_DELAY.hoverable.close,
    };
  } else if (isInDelayGroup) {
    if (isHoverable) {
      /* Individual `Tooltip` elements can opt-in to `isHoverable` even within a `TooltipGroup` where `isHoverable={false}` */

      /**
       * Even though our groupDelay _should_ always be an object (due to <TooltipGroup />), we can't enforce that in a type safe way.
       * So, we have to account for all possible types.
       */
      const newOpen =
        typeof groupDelay === 'number' ? groupDelay : (groupDelay.open ?? 0);
      delay = {
        open: newOpen,
        close: TOOLTIP_DELAY.hoverable.close,
      };
    } else {
      delay = groupDelay;
    }
  }

  const hover = useHover(context, {
    move: false,
    restMs: TOOLTIP_REST_DELAY_MS,
    delay,
  });
  const focus = useFocus(context, {
    enabled: controlledIsOpen == null,
  });
  const dismiss = useDismiss(context, {
    referencePress: shouldCloseOnClick,
    ..._useDismissProps,
  });
  const role = useRole(context, {role: 'tooltip'});

  const interactions = useInteractions([hover, focus, dismiss, role]);

  return React.useMemo(
    () => ({
      ...interactions,
      ...data,
    }),
    [interactions, data]
  );
};

export const useTooltipTransition = () => {
  const reduceMotion = usePrefersReducedMotion();

  const {context, getFloatingProps} = useTooltipContext();

  const id = useId();
  const {isInstantPhase, currentId} = useDelayGroupContext();

  useDelayGroup(context, {id});

  const duration = {
    open: isInstantPhase ? 0 : TOOLTIP_REST_DELAY_MS,
    close: isInstantPhase && currentId !== id ? 0 : TOOLTIP_REST_DELAY_MS,
  };

  const {isMounted, styles: transitionStyles} = useTransitionStyles(context, {
    duration,
    initial: ({side}) => {
      const initialStyles = {
        opacity: 0,
      };

      if (reduceMotion) {
        return initialStyles;
      }

      switch (side) {
        case 'top': {
          return {
            ...initialStyles,
            transform: `translateY(${SLIDE_DISTANCE})`,
          };
        }
        case 'bottom': {
          return {
            ...initialStyles,
            transform: `translateY(-${SLIDE_DISTANCE})`,
          };
        }
        case 'left': {
          return {
            ...initialStyles,
            transform: `translateX(${SLIDE_DISTANCE})`,
          };
        }
        case 'right': {
          return {
            ...initialStyles,
            transform: `translateX(-${SLIDE_DISTANCE})`,
          };
        }
        default: {
          return initialStyles;
        }
      }
    },
  });

  const {x, y, refs, strategy} = context;
  const {setFloating} = refs;

  return React.useMemo(
    () => ({
      x,
      y,
      setFloating,
      strategy,
      getFloatingProps,
      isMounted,
      transitionStyles,
    }),
    [isMounted, transitionStyles, x, y, setFloating, strategy, getFloatingProps]
  );
};
